/**
 * Selected-chip detail row for the Services chip editor (design spec item 4,
 * 1c envelope fields folded in). Shown below ServicesPicker when a chip is
 * selected. Publish/Quote-only + verify chip port the wizard's StepIdentity
 * services block; envelope fields (source, verified_by/at) are new here.
 * `entry` is null when no v2 bb_services row exists yet for this chip name —
 * any edit here creates one on first save (contract §4.3 autosave-per-entry).
 */
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import type { BbEntry, ServicePayload } from '@/features/brand-bible-wizard/types';
import { EnvelopeBadge, VerificationRow, type EnvelopeBadgeKind } from '@/components/brand-bible-wizard/primitives';

const SOURCE_BADGE: Record<string, EnvelopeBadgeKind> = {
  intake: 'public',
  discovered: 'discovered',
  inferred: 'low',
  performance: 'auto',
  client_edit: 'public',
};

export function ServiceChipDetailRow({
  serviceName,
  entry,
  canVerify,
  saving,
  onSave,
  onVerify,
}: {
  serviceName: string;
  entry: BbEntry<'services'> | null;
  canVerify: boolean;
  saving: boolean;
  onSave: (patch: Partial<ServicePayload>) => void;
  onVerify: () => void;
}) {
  // Controlled + local-draft: the wizard store's `saving` flag is a GLOBAL
  // in-flight indicator (any entry saving anywhere disables it if we gate on
  // it), so this text field must never be disabled by it — disabling a
  // control mid-type can defocus it without firing the blur the commit rides
  // on, silently dropping whatever the user typed. Reseed the draft only when
  // the entry's own identity/value changes (server update or chip switch —
  // the parent also remounts this component per chip via key), never while
  // the user is simply re-rendering for unrelated reasons.
  const [avgJobValueDraft, setAvgJobValueDraft] = useState(entry?.avg_job_value_range ?? '');
  useEffect(() => {
    setAvgJobValueDraft(entry?.avg_job_value_range ?? '');
  }, [entry?.id, entry?.avg_job_value_range]);

  return (
    <div className="mt-2.5 rounded-lg border border-border bg-warm-bg px-3.5 py-3">
      <div className="flex flex-wrap items-center gap-2.5 mb-2.5">
        <span className="text-[12.5px] font-semibold">{serviceName}</span>
        {entry ? (
          <EnvelopeBadge kind={SOURCE_BADGE[entry.source] ?? 'source'}>{entry.source}</EnvelopeBadge>
        ) : (
          <span className="text-[11px] text-muted-foreground">not yet tracked in the v2 bible — saving here creates it</span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3.5">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-muted-foreground">Pricing</span>
          <span className="inline-flex overflow-hidden rounded-[7px] border border-input">
            {(['publish', 'quote_only'] as const).map((p) => (
              <button
                key={p}
                type="button"
                disabled={saving}
                onClick={() => onSave({ price_policy: p })}
                className={cn(
                  'px-2.5 py-1 text-[11px] font-semibold',
                  entry?.price_policy === p ? 'bg-coral-light text-coral-dark' : 'bg-card text-teal',
                )}
              >
                {p === 'publish' ? 'Publish' : 'Quote only'}
              </button>
            ))}
          </span>
        </div>

        <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          Avg job value
          <input
            value={avgJobValueDraft}
            placeholder="e.g. $200–$450"
            onChange={(e) => setAvgJobValueDraft(e.target.value)}
            onBlur={(e) => {
              if (e.target.value !== (entry?.avg_job_value_range ?? '')) {
                onSave({ avg_job_value_range: e.target.value });
              }
            }}
            className="w-[110px] rounded-md border border-input bg-card px-2 py-1 text-[12px] outline-none focus:border-coral"
          />
        </label>

        <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <input
            type="checkbox"
            checked={Boolean(entry?.emergency)}
            disabled={saving}
            onChange={(e) => onSave({ emergency: e.target.checked })}
            className="accent-coral"
          />
          Emergency service
        </label>

        <div className="ml-auto">
          <VerificationRow
            verifiedBy={entry?.verified_by ?? null}
            verifiedAt={entry?.verified_at}
            canVerify={canVerify}
            onVerify={entry ? onVerify : undefined}
          />
        </div>
      </div>
    </div>
  );
}
